import React, { useState } from 'react';
import { CreateMonsterData } from '../types/adminContent';
import MonsterSpritePicker from './MonsterSpritePicker';

interface Props {
  title: string;
  submitLabel: string;
  formData: CreateMonsterData;
  onChange: (data: CreateMonsterData) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const MONSTER_TYPES = ['Beast', 'Undead', 'Elemental', 'Humanoid', 'Dragon', 'Construct', 'Plant', 'Fiend', 'Celestial', 'Aberration'];
const AI_PERSONALITIES = ['Aggressive', 'Defensive', 'Tactical', 'Berserker', 'Mage', 'Support', 'Coward', 'Assassin', 'Tank', 'Swarm', 'Boss'];

const CreateMonsterModal: React.FC<Props> = ({ title, submitLabel, formData, onChange, onSubmit, onClose }) => {
  const [showSpritePicker, setShowSpritePicker] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>✨ {title}</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Monster Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => onChange({ ...formData, name: e.target.value })}
                placeholder="e.g., Goblin Grunt"
              />
            </div>

            <div className="form-group">
              <label>Monster Type *</label>
              <select
                value={formData.monsterType}
                onChange={e => onChange({ ...formData, monsterType: e.target.value })}
              >
                {MONSTER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Level</label>
              <input
                type="number"
                min="1"
                value={formData.level}
                onChange={e => onChange({ ...formData, level: parseInt(e.target.value) || 1 })}
              />
            </div>

            <div className="form-group">
              <label>AI Personality</label>
              <select
                value={formData.aiPersonality}
                onChange={e => onChange({ ...formData, aiPersonality: e.target.value })}
              >
                {AI_PERSONALITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Health</label>
              <input
                type="number"
                min="1"
                value={formData.health}
                onChange={e => onChange({ ...formData, health: parseInt(e.target.value) || 1 })}
              />
            </div>

            <div className="form-group">
              <label>Damage</label>
              <input
                type="number"
                min="0"
                value={formData.damage}
                onChange={e => onChange({ ...formData, damage: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="form-group">
              <label>Defense</label>
              <input
                type="number"
                min="0"
                value={formData.defense}
                onChange={e => onChange({ ...formData, defense: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="form-group">
              <label>Speed</label>
              <input
                type="number"
                min="0"
                value={formData.speed}
                onChange={e => onChange({ ...formData, speed: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e => onChange({ ...formData, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>

            <div className="form-group full-width">
              <label>Sprite</label>
              <div className="monster-sprite-field">
                {formData.imageUrl && (
                  <img className="monster-sprite-preview" src={formData.imageUrl} alt="Selected sprite" />
                )}
                <button
                  type="button"
                  className="sprite-picker-button"
                  onClick={() => setShowSpritePicker(true)}
                >
                  🖼️ {formData.imageUrl ? 'Change Sprite' : 'Choose Sprite'}
                </button>
                {formData.imageUrl && (
                  <button
                    type="button"
                    className="sprite-picker-button"
                    onClick={() => onChange({ ...formData, imageUrl: null })}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => onChange({ ...formData, description: e.target.value })}
                placeholder="Describe this monster..."
                rows={3}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button
            className="create-button"
            onClick={onSubmit}
            disabled={!formData.name}
          >
            ✨ {submitLabel}
          </button>
        </div>
      </div>

      {showSpritePicker && (
        <MonsterSpritePicker
          onSelect={url => onChange({ ...formData, imageUrl: url })}
          onClose={() => setShowSpritePicker(false)}
        />
      )}
    </div>
  );
};

export default CreateMonsterModal;
